import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import { useIsMobile } from '../hooks/useWindowWidth';
import { COUNTRIES, SIZES } from '../constants';
import { useCompany, QK } from '../queries';

const s = {
  page: { maxWidth: 720, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  subheading: { fontSize: 15, color: '#64748b', marginBottom: 32 },
  card: { background: '#fff', borderRadius: 12, padding: '28px 32px', border: '1px solid #e2e8f0', marginBottom: 24 },
  cardMobile: { background: '#fff', borderRadius: 12, padding: '20px 16px', border: '1px solid #e2e8f0', marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#1e293b', marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  rowMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 },
  field: { marginBottom: 20 },
  fieldMobile: { marginBottom: 16 },
  label: { display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 },
  hint: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
  input: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  inputError: {
    width: '100%', padding: '10px 14px', border: '1px solid #f87171',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  fieldError: { fontSize: 12, color: '#b91c1c', marginTop: 4 },
  select: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', background: '#fff', boxSizing: 'border-box',
  },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  checkboxLabel: { fontSize: 14, color: '#374151' },
  actions: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  saveBtn: (disabled) => ({
    background: disabled ? '#93c5fd' : '#1e40af', color: '#fff', border: 'none', fontWeight: 600,
    fontSize: 15, padding: '12px 28px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  profilesLink: {
    display: 'inline-block', marginTop: 16, fontSize: 14, color: '#1e40af', cursor: 'pointer',
    textDecoration: 'none', background: 'none', border: 'none', padding: 0,
  },
  loadingPage: { textAlign: 'center', padding: 80, color: '#64748b' },
  infoBox: {
    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
    padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#0369a1',
  },
};

// Administrative + verdict-context fields only (2026-07-19 simplification):
// sector moved to the search profile; certifications and working languages
// were removed from matching entirely.
const EMPTY_FORM = {
  company_name: '', country: '', company_size: '',
  experience_years: 0, eu_projects: false,
};

export default function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only ever navigate to an in-app absolute path. A single leading slash
  // followed by a non-slash, non-backslash char rejects "//evil.com",
  // "\\evil.com" and "https://evil.com" — an attacker-supplied ?next= must not
  // be able to walk a just-authenticated user off the domain.
  const rawNext = searchParams.get('next');
  const nextPath = /^\/[^/\\]/.test(rawNext || '') ? rawNext : null;
  const showToast = useToast();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const companyQuery = useCompany({ retry: false }); // 404 = no company yet, expected
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const seededRef = useRef(false);

  // Seed the editable form once from the cached/fetched company record. The ref
  // guard means a background refetch (e.g. on tab refocus) never clobbers the
  // user's in-progress edits.
  useEffect(() => {
    if (seededRef.current || companyQuery.isLoading) return;
    if (companyQuery.data) {
      // Seed only the fields this form still edits — the API row may carry
      // legacy columns (sector, certifications, languages) we no longer send.
      const d = companyQuery.data;
      setForm({
        company_name: d.company_name || '',
        country: d.country || '',
        company_size: d.company_size || '',
        experience_years: d.experience_years || 0,
        eu_projects: !!d.eu_projects,
      });
    }
    seededRef.current = true;
  }, [companyQuery.isLoading, companyQuery.data]);

  const loading = companyQuery.isLoading;

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const errors = {};
    if (!form.company_name.trim()) errors.company_name = 'Company name is required';
    if (!form.country) errors.country = 'Country is required';
    return errors;
  }

  async function handleSave(e) {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setSaving(true);
    try {
      await apiFetch('/company', { method: 'PUT', body: JSON.stringify(form) });
      queryClient.invalidateQueries({ queryKey: QK.company });
      queryClient.invalidateQueries({ queryKey: QK.matches });
      showToast('Company info saved.', 'success');
      if (nextPath) navigate(nextPath);
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={s.loadingPage}>Loading…</div>;

  const card = isMobile ? s.cardMobile : s.card;
  const row = isMobile ? s.rowMobile : s.row;
  const field = isMobile ? s.fieldMobile : s.field;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>{nextPath ? 'First, set up your company' : 'Company Settings'}</h1>
      <p style={s.subheading}>
        {nextPath
          ? 'This info is shared across all your search profiles. Save it once and you\'ll be taken straight to your new profile.'
          : 'This information is shared across all your search profiles and used to improve match quality.'}
      </p>

      {!nextPath && (
        <div style={s.infoBox}>
          Company info is filled once here. To create or manage individual search profiles (each targeting a specific product or service area), go to{' '}
          <button style={{ ...s.profilesLink, display: 'inline' }} onClick={() => navigate('/profiles')}>Search Profiles</button>.
        </div>
      )}

      <form onSubmit={handleSave}>
        <div style={card}>
          <div style={s.sectionTitle}>Basic Information</div>
          <div style={row}>
            <div style={field}>
              <label style={s.label}>Company name *</label>
              <input
                style={fieldErrors.company_name ? s.inputError : s.input}
                value={form.company_name}
                onChange={(e) => set('company_name', e.target.value)}
              />
              {fieldErrors.company_name && <div style={s.fieldError}>{fieldErrors.company_name}</div>}
            </div>
            <div style={field}>
              <label style={s.label}>Country of registration *</label>
              <select
                style={fieldErrors.country ? { ...s.select, border: '1px solid #f87171' } : s.select}
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {fieldErrors.country && <div style={s.fieldError}>{fieldErrors.country}</div>}
            </div>
          </div>
          <div style={row}>
            <div style={field}>
              <label style={s.label}>Company size</label>
              <select style={s.select} value={form.company_size} onChange={(e) => set('company_size', e.target.value)}>
                <option value="">Select size</option>
                {SIZES.map((sz) => <option key={sz} value={sz}>{sz} employees</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={card}>
          <div style={s.sectionTitle}>Experience</div>
          <div style={row}>
            <div style={field}>
              <label style={s.label}>Years of experience</label>
              <input
                style={s.input}
                type="number" min="0" max="100"
                value={form.experience_years}
                onChange={(e) => set('experience_years', parseInt(e.target.value) || 0)}
              />
            </div>
            <div style={field}>
              <label style={s.label}>&nbsp;</label>
              <div style={s.checkboxRow}>
                <input type="checkbox" id="eu_projects" checked={form.eu_projects} onChange={(e) => set('eu_projects', e.target.checked)} />
                <label htmlFor="eu_projects" style={s.checkboxLabel}>Previous EU-funded project experience</label>
              </div>
            </div>
          </div>
        </div>

        <div style={s.actions}>
          <button style={s.saveBtn(saving)} type="submit" disabled={saving}>
            {saving ? 'Saving...' : nextPath ? 'Save & continue' : 'Save company info'}
          </button>
        </div>
      </form>
    </div>
  );
}
